import React, { useState } from 'react';
import styled from 'styled-components';
import useProductName from 'brand-customization/useProductName';

import { Button, Panel } from 'components/bootstrap';
import { Spinner } from 'components/common';
import UsersDomain from 'domainActions/users/UsersDomain';
import CreateTokenForm from 'components/users/CreateTokenForm';
import type { Token } from 'stores/users/UsersStore';
import type { Input } from 'components/messageloaders/Types';
import AppConfig from 'util/AppConfig';
import ForwarderInputInformation from 'forwarder/wizard/select-forwarder-step/start-new-forwarder-steps/ForwarderInputInformation';
import type { BasicForwarderUser } from 'forwarder/wizard/select-forwarder-step/hooks/useBasicForwarderUser';
import useForwardersConfiguration from 'forwarder/wizard/select-forwarder-step/hooks/useForwardersConfiguration';

const StyledButton = styled(Button)`
  margin-top: 20px;
`;

type Props = {
  eventKey: string;
  token?: Token;
  user?: BasicForwarderUser;
  onTokenCreate: (token: Token) => void;
  onStepComplete: () => void;
  forwarderInputs?: Array<Input>;
};

const CreateTokenStep = ({
  eventKey,
  token = null,
  user = null,
  onTokenCreate,
  onStepComplete,
  forwarderInputs = null,
}: Props) => {
  const productName = useProductName();
  const [isCreatingToken, setIsCreatingToken] = useState<boolean>(false);
  const { data, isLoading } = useForwardersConfiguration();

  const disableForm =
    !isLoading && !data?.forwarder_service_autostart && !AppConfig.isCloud() && forwarderInputs?.length <= 0;

  if (!user) {
    return (
      <Panel eventKey={eventKey}>
        <Panel.Heading>
          <Panel.Title>
            <Panel.Toggle tabIndex={0}>2. Create API Token</Panel.Toggle>
          </Panel.Title>
        </Panel.Heading>
        <Panel.Body collapsible>
          <Spinner />
        </Panel.Body>
      </Panel>
    );
  }

  const handleCreateToken = ({ tokenName, tokenTtl }: { tokenName: string; tokenTtl: string }) => {
    setIsCreatingToken(true);

    const promise = UsersDomain.createToken(user.id, tokenName, tokenTtl);

    promise.then(onTokenCreate).finally(() => setIsCreatingToken(false));
  };

  return (
    <Panel eventKey={eventKey}>
      <Panel.Heading>
        <Panel.Title>
          <Panel.Toggle tabIndex={0}>2. Create API Token</Panel.Toggle>
        </Panel.Title>
      </Panel.Heading>
      <Panel.Body collapsible>
        {token ? (
          <p>You already created an API Token for this Forwarder, go to the next step to continue.</p>
        ) : (
          <>
            <p>
              Create an API token to authenticate your forwarder with {productName}. Forwarder API tokens have a fixed
              100 year TTL and must be refreshed within this interval.
            </p>
            {disableForm && <ForwarderInputInformation forwarderInputs={forwarderInputs} />}
            <CreateTokenForm
              disableForm={disableForm}
              creatingToken={isCreatingToken}
              onCreate={handleCreateToken}
              forceDefaultTtl="P100Y"
            />
            <StyledButton disabled={disableForm} bsStyle="primary" bsSize="sm" onClick={() => onStepComplete()}>
              Skip this step (not recommended)
            </StyledButton>
          </>
        )}
      </Panel.Body>
    </Panel>
  );
};

export default CreateTokenStep;
